import { Button, Grid, TextField} from '@tunasong/ui-lib'
import { getDefaultEntityName, logger, shortUuid } from '@tunasong/models'
import type { Audio, Entity, Persisted } from '@tunasong/schemas'
import { isSong } from '@tunasong/schemas'
import { HBox, TunaEntityBreadcrumbs, Typography, VBox, useAlert } from '@tunasong/ui-lib'
import type { ChangeEvent, FC, HTMLAttributes } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { usePermission } from '../audio-permissions.hook.js'
import AudioPermissions from '../audio-permissions.js'
import { useMixer, useRecorder, useStartMic, useTransport } from '../hooks/index.js'
import { Meter } from '../meter.js'
import RecordingButton from '../recording-button.js'
import { Time } from '../time.js'
import { Waveform } from '../waveform/waveform.js'
import { FFT } from './fft.js'
import styles from './audio-recorder.module.css'

export interface RecorderProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  /** The parent entity for the audio. If specified, the recording experience can be customized, e.g., count-in tempo for songs */
  parentEntity?: Persisted<Entity>
  showMeter?: boolean
  autoFocus?: boolean
  /** The maximum recording time, in seconds */
  maxSeconds?: number
  /** Use either MediaRecorder (Webm / Opus) or Webaudio worklet Wav recorder. @default stream */
  recorderType?: 'stream' | 'wav'
  /** Called during recording */
  onRecording?(recording: boolean): void
  onComplete(name: string, audio: Blob): void
}

const createTemporaryMedia = ({ url, parentId }: { url: string; parentId?: string }) =>
  ({
    id: shortUuid(),
    parentId,
    name: getDefaultEntityName('audio'),
    type: 'audio',
    userId: 'temporary-media',
    storageUrls: {
      url,
      expiresAtISODateTime: '2100-01-01T00:00:00.000Z',
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }) satisfies Persisted<Audio>

export const AudioRecorder: FC<RecorderProps> = props => {
  const {
    className,
    parentEntity,
    showMeter = true,
    autoFocus = false,
    onRecording,
    onComplete,
    recorderType = 'stream',
    maxSeconds = 60 * 60,
    ...restProps
  } = props
  const mixer = useMixer()

  const { selectedDevice } = useStartMic()

  const mediaRecorder = useRecorder(recorderType)
  const { transport } = useTransport()
  const [audioData, setAudioData] = useState<Blob>()
  const [temporaryMedia, setTemporaryMedia] = useState<Persisted<Audio>>()

  const [isRecording, setIsRecording] = useState(false)

  // Revoke the recording's preview blob URL when it's replaced (new recording),
  // cleared (save), or the recorder unmounts — otherwise each recording pins a
  // multi-MB Blob for the page lifetime (memory-analysis S2, #1639). The url is
  // preview-only (the `Waveform`); save/upload uses the `audioData` Blob, not it.
  const previewUrl = temporaryMedia?.storageUrls?.url
  useEffect(() => {
    if (!previewUrl) {
      return
    }
    return () => URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const handleSave = (audioData: Blob) => () => {
    const name = temporaryMedia?.name ?? getDefaultEntityName('audio')
    setAudioData(undefined)
    setTemporaryMedia(undefined)
    onComplete(name, audioData)
  }

  useEffect(() => {
    if (!onRecording) {
      return
    }
    onRecording(isRecording)
  }, [isRecording, onRecording])

  /** Example https://developer.mozilla.org/en-US/docs/Web/API/MediaStream_Recording_API/Using_the_MediaStream_Recording_API */
  const startRecording = async () => {
    if (!mediaRecorder) {
      throw new Error(`Cannot record without a mixer and a mediaRecorder`)
    }

    logger.debug(`Starting transport for recording. Recording type: ${recorderType}`)
    transport.start()
    mediaRecorder.start()

    setIsRecording(true)
  }

  const handleDelete = () => {
    setAudioData(undefined)
    setIsRecording(false)
  }

  const stopRecording = useCallback(() => {
    if (!mediaRecorder) {
      return
    }
    mediaRecorder.stop()
    transport.stop()

    const audioData = mediaRecorder.getRecording()

    logger.debug('Completed recording. Pending upload....')
    const url = URL.createObjectURL(audioData)
    setAudioData(audioData)
    const temporaryMedia = createTemporaryMedia({ url, parentId: parentEntity?.id })
    setTemporaryMedia(temporaryMedia)
    setIsRecording(false)
  }, [mediaRecorder, parentEntity?.id, transport])

  const handleName = (ev: ChangeEvent<HTMLInputElement>) => {
    ev.preventDefault()
    const value = ev.target.value.trim()
    if (!value || !temporaryMedia) {
      return
    }
    setTemporaryMedia({
      ...temporaryMedia,
      name: value,
    })
  }

  /** Stop the recording when maxSeconds has been recorded */
  const { alert } = useAlert()
  useEffect(() => {
    if (!isRecording) {
      return
    }
    const id = setTimeout(() => {
      alert({
        severity: 'warning',
        message: `Recording stopped after ${maxSeconds} seconds. This is currently the maximum recording time.`,
      })
      stopRecording()
    }, 1000 * maxSeconds)
    return () => clearTimeout(id)
  }, [alert, isRecording, maxSeconds, stopRecording])

  const { disabled } = usePermission('microphone')

  return (
    <VBox {...restProps}>
      <AudioPermissions />

      {temporaryMedia && audioData ? (
        <>
          <Waveform media={temporaryMedia} renderPeaks={true} timeline={false} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <TextField
                className={styles.selfCenter}
                size="small"
                variant="standard"
                label="Recording name"
                defaultValue={temporaryMedia.name}
                onChange={handleName}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <div className={styles.saveInfo}>
                <Typography className={styles.saveInfoText} variant="body2">
                  Recording will be stored at
                </Typography>
                <TunaEntityBreadcrumbs entity={temporaryMedia} maxItems={10} />
              </div>
            </Grid>
          </Grid>

          <HBox className={styles.actions}>
            <Button color={'secondary'} onClick={handleDelete}>
              Discard
            </Button>
            <Button onClick={handleSave(audioData)} autoFocus={autoFocus}>
              Save
            </Button>
          </HBox>
        </>
      ) : (
        <>
          {selectedDevice ? <Typography variant="caption">{selectedDevice.label}</Typography> : null}
          <div className={styles.spacer} />
          <VBox className={styles.fftContainer}>
            <FFT className={styles.fft} />
          </VBox>
          <div className={styles.spacer} />

          {showMeter && !disabled && (
            <div className={styles.meterRow}>
              <Meter controller={mixer?.getBus('record')} />
              <div className={styles.spacer} />
              <Time startTime={mediaRecorder?.startTime} />
            </div>
          )}
          <RecordingButton
            className={className}
            disabled={disabled}
            label="Start Recording"
            rhythm={isSong(parentEntity) ? parentEntity.rhythm : undefined}
            onStart={startRecording}
            onStop={stopRecording}
          />
        </>
      )}
    </VBox>
  )
}

export default AudioRecorder
